import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Collapse, Descriptions, Input, InputNumber, Popconfirm, Radio, Select, Space, Spin, Table, Tag, Typography, message } from 'antd';
import { DeleteOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import tmAdminExtService from '../../services/tmAdminExtService';
import { fmtTime, ok, showSecretOnce, toastErr } from './common';

const { Text } = Typography;

const toRows = (obj) => Object.entries(obj || {}).map(([domain, v], i) => ({ key: `${domain}-${i}`, domain, conns: v.conns, per_min: v.per_min }));

/**
 * Node operations: status (active / draining / disabled), outbound IPs, per-recipient-domain limits, health, secret rotation.
 * Mounted in Nodes.jsx (运维 drawer). Writes go through /nodes/{id}/ops/* (validated + logged).
 */
const NodeOpsPanel = ({ nodeId, onChanged }) => {
    const [n, setN] = useState(null);
    const [rows, setRows] = useState([]);
    const [ips, setIps] = useState([]);
    const [busy, setBusy] = useState(false);
    const [checking, setChecking] = useState(false);

    const load = useCallback(async () => {
        if (!nodeId) return;
        try {
            const r = await tmAdminExtService.nodeOps(nodeId);
            const d = r?.data || null;
            setN(d);
            setRows(toRows(d?.domain_limits));
            setIps(d?.bind_ips || []);
        } catch (e) {
            toastErr(e, '加载失败');
        }
    }, [nodeId]);

    useEffect(() => { load(); }, [load]);

    const update = async (data, text = '已保存') => {
        setBusy(true);
        try {
            const r = await tmAdminExtService.updateNodeOps(nodeId, data);
            if (ok(r)) { message.success(text); load(); onChanged && onChanged(); }
        } catch (e) {
            toastErr(e, '保存失败');
        } finally {
            setBusy(false);
        }
    };

    const saveLimits = () => {
        const out = {};
        for (const r of rows) {
            const d = (r.domain || '').trim().toLowerCase();
            if (!d) continue;
            if (out[d]) { message.error(`重复：${d}`); return; }
            if (!r.conns || !r.per_min) { message.error(`${d} 未填完整`); return; }
            out[d] = { conns: r.conns, per_min: r.per_min };
        }
        update({ domain_limits: Object.keys(out).length ? out : null });
    };

    const health = async () => {
        setChecking(true);
        try {
            const r = await tmAdminExtService.nodeHealth(nodeId);
            if (ok(r)) { message.success('节点在线'); load(); }
        } catch (e) {
            toastErr(e, '节点不可达', true);
            load();
        } finally {
            setChecking(false);
        }
    };

    const rotate = async () => {
        try {
            const r = await tmAdminExtService.rotateNodeSecret(nodeId);
            if (ok(r)) showSecretOnce('新节点密钥', r.data.secret, '更新节点 /etc/yeap-tm/node.json 后重启 tmnode');
        } catch (e) {
            toastErr(e, '操作失败');
        }
    };

    if (!n) return <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>;

    const setRow = (key, patch) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    const h = n.last_health || {};
    const defaults = n.default_domain_limits || {};

    return (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Space wrap>
                <Text strong>#{n.id} {n.name}</Text>
                <Text type="secondary">{n.ip}</Text>
                {n.healthy ? <Tag color="green">同步正常</Tag> : <Tag color="red">未同步</Tag>}
                <Text type="secondary" style={{ fontSize: 12 }}>v{n.last_sync_version ?? '-'} / v{n.sync_version} · {fmtTime(n.last_sync_at)}</Text>
                <Button size="small" icon={<ReloadOutlined />} onClick={load} />
            </Space>

            <Card size="small" title="状态">
                <Radio.Group value={n.status} disabled={busy} onChange={(e) => update({ status: e.target.value }, '状态已更新')} optionType="button" buttonStyle="solid"
                    options={[{ value: 'active', label: 'active' }, { value: 'draining', label: 'draining' }, { value: 'disabled', label: 'disabled' }]} />
                <Text type="secondary" style={{ fontSize: 12, marginInlineStart: 8 }}>draining：不再分配新邮件，队列继续投完</Text>
            </Card>

            <Card size="small" title="出站 IP" extra={<Button size="small" type="primary" loading={busy} onClick={() => update({ bind_ips: ips })}>保存</Button>}>
                <Select mode="tags" value={ips} onChange={setIps} tokenSeparators={[',', ' ']} style={{ width: '100%' }} placeholder={`留空 = ${n.ip}`} />
            </Card>

            <Card size="small" title="收件域限速" extra={
                <Space>
                    <Button size="small" icon={<PlusOutlined />} onClick={() => setRows((rs) => [...rs, { key: `new-${Date.now()}`, domain: '', conns: 2, per_min: 30 }])}>添加</Button>
                    <Button size="small" type="primary" loading={busy} onClick={saveLimits}>保存</Button>
                </Space>
            }>
                <Table size="small" rowKey="key" pagination={false} dataSource={rows} locale={{ emptyText: '使用默认值' }} columns={[
                    { title: '收件域（* = 其它）', dataIndex: 'domain', render: (v, r) => <Input size="small" value={v} placeholder="gmail.com" onChange={(e) => setRow(r.key, { domain: e.target.value })} /> },
                    { title: '并发', dataIndex: 'conns', width: 110, render: (v, r) => <InputNumber size="small" min={1} max={100} value={v} onChange={(x) => setRow(r.key, { conns: x })} /> },
                    { title: '每分钟', dataIndex: 'per_min', width: 130, render: (v, r) => <InputNumber size="small" min={1} max={100000} value={v} onChange={(x) => setRow(r.key, { per_min: x })} /> },
                    { title: '默认', key: 'd', width: 110, render: (_, r) => (defaults[r.domain] ? <Text type="secondary" style={{ fontSize: 12 }}>{defaults[r.domain].conns} / {defaults[r.domain].per_min}</Text> : null) },
                    { title: '', key: 'x', width: 40, render: (_, r) => <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))} /> },
                ]} />
                <Collapse ghost size="small" items={[{
                    key: 'eff', label: '生效值（默认 + 覆盖）',
                    children: (
                        <Space size={[4, 4]} wrap>
                            {Object.entries(n.effective_domain_limits || {}).map(([d, v]) => <Tag key={d}>{d} {v.conns}/{v.per_min}</Tag>)}
                        </Space>
                    ),
                }]} />
            </Card>

            <Card size="small" title="健康" extra={<Button size="small" loading={checking} onClick={health}>实时检测</Button>}>
                {n.last_health ? (
                    <>
                        <Descriptions size="small" column={2} bordered items={[
                            { key: 'q', label: '队列深度', children: h.queue_depth ?? '-' },
                            { key: 'o', label: '最老消息', children: h.oldest_age_s !== undefined ? `${h.oldest_age_s}s` : '-' },
                            { key: 'v', label: '同步版本', children: h.version ?? '-' },
                            { key: 't', label: '检测于', children: fmtTime(n.last_health_at) },
                            ...(h.error ? [{ key: 'e', label: '错误', span: 2, children: <Text type="danger">{h.error}</Text> }] : []),
                        ]} />
                        <Collapse ghost size="small" items={[{
                            key: 'raw', label: '原始 JSON',
                            children: <pre style={{ fontSize: 12, maxHeight: 300, overflow: 'auto', margin: 0 }}>{JSON.stringify(n.last_health, null, 2)}</pre>,
                        }]} />
                    </>
                ) : <Text type="secondary">尚未检测</Text>}
            </Card>

            <Popconfirm title="旧密钥立即失效，节点需更新 node.json" onConfirm={rotate}>
                <Button danger>轮换节点密钥</Button>
            </Popconfirm>
        </Space>
    );
};

export default NodeOpsPanel;
